import React, { useEffect, useState } from 'react';
import { walletAPI, Wallet as WalletType } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { 
  CurrencyDollarIcon, 
  PlusIcon, 
  ClockIcon,
  CheckCircleIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import AddFunds from '../Customer/AddFunds';

interface Transaction {
  id: number;
  type: string;
  amount: number;
  description: string;
  created_at: string;
}

const Wallet: React.FC = () => {
  const { formatPrice, currency } = useCurrency();
  const [wallet, setWallet] = useState<WalletType | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddFunds, setShowAddFunds] = useState(false);

  const fetchWallet = async () => {
    try {
      const walletData = await walletAPI.getWallet();
      setWallet(walletData);
      setTransactions(walletData.recent_transactions || []);
    } catch (err: any) {
      console.error('Failed to load wallet information', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const handleAddFundsSuccess = () => {
    setShowAddFunds(false);
    fetchWallet(); // Refresh wallet balance and transactions
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-4 border-gray-200 border-t-volexx-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Wallet</h1>
          <p className="mt-2 text-gray-600">Manage your account balance and view transaction history.</p>
        </div>

        {/* Wallet Balance Card */}
        <div className="bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 rounded-volexx-lg shadow-volexx-lg p-8 text-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium mb-2">Current Balance</h2>
              <p className="text-4xl font-bold">
                {formatPrice(wallet?.balance || 0)}
              </p>
            </div>
            <CurrencyDollarIcon className="h-16 w-16 text-volexx-blue-200" />
          </div>
          
          {/* Add Funds Button */}
          <div className="mt-6">
            <button
              onClick={() => setShowAddFunds(true)}
              className="bg-white text-volexx-blue-600 font-bold py-3 px-6 rounded-volexx hover:bg-gray-50 transition-colors flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Funds
            </button>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Transactions</h3>
          
          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div 
                  key={transaction.id} 
                  className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'credit' 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                    }`}>
                      {transaction.type === 'credit' ? (
                        <PlusIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <MinusIcon className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {transaction.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${
                      transaction.type === 'credit' 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {transaction.type === 'credit' ? '+' : '-'}
                      {formatPrice(transaction.amount)}
                    </p>
                    <div className="flex items-center justify-end">
                      <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                      <span className="text-xs text-gray-500">Completed</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No transactions yet</p>
              <p className="text-sm text-gray-400 mt-1">Your transaction history will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Funds Modal */}
      {showAddFunds && (
        <AddFunds 
          onClose={() => setShowAddFunds(false)}
          onSuccess={handleAddFundsSuccess}
        />
      )}
    </div>
  );
};

export default Wallet;
